import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/material";
import { useRef } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";

import { BlFormDropdown } from "Forms/Components/BlFormDropdown";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { useDefaultValues } from "Forms/Hooks/useDefaultValues";
import useHandleServerError from "Forms/Hooks/useHandleServerError";
import { getPriceCategoryTypeOptions } from "Forms/Utils/EnumOptions";
import type { PriceCategoryListItemDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  type PriceCategoryFormData,
  priceCategoryFormSchema,
} from "PriceCategories/Forms/priceCategoryFormSchema";

export interface PriceCategoryFormHandle {
  submit: (
    onValid: (data: PriceCategoryFormData) => void | Promise<void>,
  ) => () => void;
  reset: () => void;
}

interface PriceCategoryFormProps {
  priceCategory?: PriceCategoryListItemDto | null;
  formRef: React.RefObject<PriceCategoryFormHandle | null>;
  serverError?: Error | null;
}

export const PriceCategoryForm = ({
  priceCategory,
  formRef,
  serverError,
}: PriceCategoryFormProps): React.ReactNode => {
  const { t } = useResource();

  const internalFormRef = useRef<UseFormReturn<PriceCategoryFormData> | null>(
    null,
  );

  const defaultValues = useDefaultValues<PriceCategoryFormData>(
    () => buildDefaults(priceCategory),
    [priceCategory],
    internalFormRef,
  );

  const form = useForm<PriceCategoryFormData>({
    resolver: zodResolver(priceCategoryFormSchema),
    defaultValues,
  });

  internalFormRef.current = form;

  useHandleServerError({
    form,
    error: serverError,
    formKey: "name",
    resource: Resources.ApiErrors.SavePriceCategory,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = form;

  formRef.current = {
    submit: (onValid) => () => handleSubmit(onValid)(),
    reset: () => reset(),
  };

  return (
    <Stack spacing={4}>
      <BlFormInput
        control={control}
        name="name"
        label={t(Resources.PriceCategories.CreateModal.Name)}
        errors={errors}
        placeholder={t(Resources.PriceCategories.CreateModal.NamePlaceholder)}
        required
      />
      <BlFormDropdown
        control={control}
        name="type"
        label={t(Resources.PriceCategories.Type)}
        options={getPriceCategoryTypeOptions()}
        errors={errors}
        placeholder={t(Resources.PriceCategories.CreateModal.TypePlaceholder)}
        required
      />
      <BlFormInput
        control={control}
        name="commissionRate"
        label={t(Resources.PriceCategories.CommissionRate)}
        errors={errors}
        isNumber
        required
        unit="%"
      />
      <BlFormInput
        control={control}
        name="defaultPrice"
        label={t(Resources.PriceCategories.DefaultPrice)}
        errors={errors}
        isNumber
        required
        unit="€"
      />
    </Stack>
  );
};

function buildDefaults(
  priceCategory?: PriceCategoryListItemDto | null,
): PriceCategoryFormData {
  if (!priceCategory) {
    return { name: "", type: "", commissionRate: 0, defaultPrice: 0 };
  }
  return {
    name: priceCategory.name,
    type: priceCategory.type,
    commissionRate: (priceCategory.commissionRate ?? 0) * 100,
    defaultPrice: priceCategory.defaultPrice,
  };
}
